import { CircularProgress, Card, Box, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAnnualPlanUserRole } from '../../../../../hooks/use-annualPlanUserRole'
import { useUpdatePlanOrgTaskStatus } from '../../../../../hooks/use-updatePlanOrgTaskStatus'
import { CommentParentType } from '../../../../../models/Comment'
import { type PlanOrganizationTaskStatus, type PlanOrganizationTask, PlanOrganizationTaskName } from '../../../../../models/PlanOrganizationTask'
import {
  GetPlanOrganizationTask
} from '../../../../../services/AnnualPlanService'
import { Comments } from '../../../../customComponents/comments/Comments'
import { MeetingSchedules } from '../../MeetingSchedules'
import { SupportCosts } from './SupportCosts'
import { LockedBanner } from '../../LockedBanner'
import { RCNOperationsStatusButton } from './RCNOperationsStatusButton'
import { handleApiResponse } from '../../../../../utils/apiHelpers'

interface Props {
  annualPlanId: number
  rcnId: number
  showComments?: boolean
  onUpdateStatus: () => void
}

export function RCNOperationsOverview (props: Props): JSX.Element {
  const taskName = PlanOrganizationTaskName.RcnOperations
  const { planId } = useParams()
  const { userRole } = useAnnualPlanUserRole(planId != null ? Number(planId) : 0)
  const [isLoading, setIsLoading] = useState(true)
  const [planOrganizationTask, setPlanOrganizationTask] = useState<PlanOrganizationTask>()
  const isImplementationPhase = window.location.pathname.includes('Implementation')

  const { disableStatus, readOnly, trackingReadonly, isTrackingPhase } = useUpdatePlanOrgTaskStatus({
    userRole,
    annualPlanId: Number(planId),
    planOrganizationTask,
    contractPhase: !isImplementationPhase
  })

  const fetchTask = async (): Promise<void> => {
    const task = await handleApiResponse(GetPlanOrganizationTask(props.annualPlanId, props.rcnId, taskName))
    if (task != null) setPlanOrganizationTask(task)
  }

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      await fetchTask()
      setIsLoading(false)
    }

    void fetchData()
  }, [])

  const handleStatusChange = async (status: PlanOrganizationTaskStatus): Promise<void> => {
    if (planOrganizationTask == null) return
    const updatedTask = { ...planOrganizationTask, status }
    setPlanOrganizationTask(updatedTask)
    props.onUpdateStatus()
  }

  if (isLoading) {
    return <CircularProgress />
  }

  return <>
    {readOnly && (
      <LockedBanner status={planOrganizationTask?.status} isTrackingPhase={isTrackingPhase} />
    )}

    <Card sx={{
      backgroundColor: 'var(--grey-100)',
      borderRadius: '12px',
      padding: '20px 40px 40px 40px',
      display: 'flex',
      flexDirection: 'column',
      gap: '40px',
      position: 'relative',
      overflow: 'visible'
    }}
    >
      <Box display='flex' flexDirection='column' gap='1em'>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant='h1'>RCN Operations</Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', float: 'right' }}>
            {planOrganizationTask != null && (
              <RCNOperationsStatusButton
                userRole={userRole}
                disableStatus={disableStatus}
                annualPlanId={Number(planId ?? 0)}
                planOrganizationTask={planOrganizationTask}
                handleStatusChange={handleStatusChange}
                implementationPhase={isImplementationPhase}
              />
            )}

            <Comments
              parentType={CommentParentType.PlanOrganizationTask}
              parentId={planOrganizationTask?.id ?? 0}
              showComments={props.showComments}
            />
          </Box>
        </Box>

        <Typography variant='body1'>
          In the Meetings table, list all RCN Meetings planned for the year. For each RCN Meeting, indicate the title, date, time, location, primary contact, and budget request. In the RCN Support Costs table, list any RCN support costs. For each support cost, provide a description and budget request. Click “Things to Consider” to review the allowable RCN support costs.
        </Typography>
      </Box>

      <MeetingSchedules
        status={planOrganizationTask?.status?.toString() ?? ''}
        annualPlanId={props.annualPlanId}
        readOnly={readOnly}
        rcnId={props.rcnId}
        isImplementationPhase={isImplementationPhase}
        trackingReadOnly={trackingReadonly}
      />

      <SupportCosts
        status={planOrganizationTask?.status?.toString() ?? ''}
        annualPlanId={props.annualPlanId}
        readOnly={readOnly}
        rcnId={props.rcnId}
        isImplementationPhase={isImplementationPhase}
        trackingReadOnly={trackingReadonly}
      />
    </Card>
  </>
}
